import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders, HttpErrorResponse} from '@angular/common/http';

import { environment } from '../../../environments/environment';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {
  ActivateTotpResponse, AllAuthLoginResponse,
  AllauthResponse, AuthFlows,
  DeactivateTotpResponse, Flow,
  ListAuthenticatorsResponse, ReauthenticateResponse,
  TOTPAuthenticatorStatusResponse
} from '../models/allauth.model';
import * as UserActions from '../../login/state/user.actions';
import {Store} from '@ngrx/store';

@Injectable({
  providedIn: 'root'
})
export class AllauthAccountsService {
  baseUrl = environment.apiUrl.slice(0, -3) + '/_allauth/app/v1';

  constructor(private http: HttpClient, private store: Store) {}

  get<T>(url: string): Observable<T> {
    return this.http.get<T>(`${url}`).pipe(
      map((response: T) => this.handleSuccess(response)),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  post<B, R>(url: string, body: B): Observable<R> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<R>(url, body, { headers }).pipe(
      map((response: R) => this.handleSuccess(response)),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  delete<T>(url: string): Observable<T> {
    return this.http.delete<T>(`${url}`).pipe(
      map((response: T) => response),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  handleSuccess<T>(response: T): T {
    const res = response as any;
    if (res.status === 200 && res.meta?.is_authenticated) {
      if (res.meta?.session_token && res.meta?.access_token) {
        this.store.dispatch(UserActions.loginSuccess({
          user: res.meta.user,
          token: res.meta.access_token,
          sessionToken: res.meta.session_token
        }));
      }
    }
    return response;
  }

  handleError(error: HttpErrorResponse): Observable<never> {
    console.error('Error occurred while fetching data:', error);
    const response = error.error as AllAuthLoginResponse;
    const reauthenticateFlow: Flow = (response?.data as AuthFlows)?.flows.find(flow => flow.id === 'reauthenticate');
    const loginFlow: Flow = (response?.data as AuthFlows)?.flows.find(flow => flow.id === 'login');

    const pendingFlow: Flow = (response?.data as AuthFlows)?.flows.find(flow => flow.is_pending);
    if (response.status === 401 && pendingFlow) {
      switch (pendingFlow.id) {
        case 'mfa_authenticate':
          this.store.dispatch(UserActions.storeSessionToken({sessionToken: response.meta.session_token}));
          this.store.dispatch(UserActions.totpLoginRedirect());
          break;
        case 'mfa_reauthenticate':
          this.store.dispatch(UserActions.storeSessionToken({sessionToken: response.meta.session_token}));
          this.store.dispatch(UserActions.totpLoginRedirect());
          break;
        default:
          break;
      }
    } else if (response.status === 401 && reauthenticateFlow) {
      this.store.dispatch(UserActions.reauthenticateRedirect());
    } else if (response.status === 401 && loginFlow) {
      this.store.dispatch(UserActions.loginRedirect());
    }

    if (response.status === 410) {
      this.store.dispatch(UserActions.logout());
    }

    throw error; // rethrow the error for further handling
  }

  getAuthenticators(): Observable<ListAuthenticatorsResponse> {
    return this.get<ListAuthenticatorsResponse>(`${this.baseUrl}/account/authenticators`);
  }

  getTotpAuthenticator(): Observable<TOTPAuthenticatorStatusResponse> {
    return this.get<TOTPAuthenticatorStatusResponse>(`${this.baseUrl}/account/authenticators/totp`);
  }

  activateTotp(code: string): Observable<ActivateTotpResponse> {
    const url = `${this.baseUrl}/account/authenticators/totp`;
    const body = JSON.stringify({ code });
    return this.post<string, ActivateTotpResponse>(url, body);
  }

  deactivateTotp(): Observable<DeactivateTotpResponse> {
    const url = `${this.baseUrl}/account/authenticators/totp`;
    return this.delete<DeactivateTotpResponse>(url);
  }

  reauthenticate(password: string): Observable<ReauthenticateResponse> {
    const url = `${this.baseUrl}/auth/reauthenticate`;
    const body = JSON.stringify({ password });
    return this.post<string, ReauthenticateResponse>(url, body);
  }

  getEmailAddresses(): Observable<AllauthResponse> {
    const url = `${this.baseUrl}/account/email`;
    return this.get<AllauthResponse>(url);
  }

  changePassword(oldPassword: string, newPassword: string): Observable<AllauthResponse> {
    const url = `${this.baseUrl}/account/password/change`;
    const body = JSON.stringify({ current_password: oldPassword, new_password: newPassword });
    return this.post<string, AllauthResponse>(url, body);
  }
}
